/**
 * Geographic region of a Power BI artifact's data.
 *
 * Region is never stated by Power BI on a workspace — it comes from the
 * capacity the workspace sits on, or from the tenant's home region when the
 * workspace is on shared capacity. That distinction matters during a data
 * residency review, so an inferred region is drawn differently from one read
 * off a capacity record rather than being passed off as the same fact.
 */
import React from "react";

export type RegionSource =
  | "capacity"
  | "tenant-home"
  | "unresolved-capacity"
  | "unknown"
  | string;

interface RegionBadgeProps {
  region?: string;
  source?: RegionSource;
  /** Capacity name and SKU, shown in the tooltip when known. */
  capacityName?: string;
  capacitySku?: string;
  className?: string;
}

export default function RegionBadge({
  region,
  source,
  capacityName,
  capacitySku,
  className = "",
}: RegionBadgeProps) {
  if (!region) {
    if (source === "unresolved-capacity") {
      return (
        <span
          className={`inline-block rounded-full bg-warning-subtle px-2 py-0.5 text-xs font-medium text-warning ${className}`}
          title="This workspace is on a capacity we cannot read, so its region is unknown. Grant the collector capacity admin rights to resolve it."
        >
          Unresolved
        </span>
      );
    }
    return <span className={`text-xs text-content-muted ${className}`}>—</span>;
  }

  const inferred = source === "tenant-home";
  const tooltip = inferred
    ? "On shared capacity, so the data lives in the tenant's home region"
    : [capacityName, capacitySku && `SKU ${capacitySku}`].filter(Boolean).join(" · ") ||
      undefined;

  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        inferred
          ? "bg-surface-hover text-content-muted"
          : "bg-accent-subtle text-accent",
        className,
      ].join(" ")}
      title={tooltip}
    >
      {region}
      {inferred && <span aria-hidden="true">*</span>}
    </span>
  );
}
